<script setup>
import { computed, ref } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import { useToastStore } from '@/stores/Toast'
import { useDriverStore } from '@/stores/Drivers'
import { EMAIL_DRIVER } from '@/constants/dispatch'

/**
 * The store instances.
 */
const uiStore = useUiStore()
const toastStore = useToastStore()
const driverStore = useDriverStore()

/**
 * The component's local refs.
 */
const isLoading = ref(false)
const selectedDrivers = ref([])

/**
 * The load's data.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(EMAIL_DRIVER)
})

/**
 * The load's drivers.
 *
 * @return {Array}
 */
const drivers = computed(() => {
    return data.value.drivers
})

/**
 * Determine if the load has drivers.
 *
 * @return {Boolean}
 */
const hasDrivers = computed(() => {
    return drivers.value.length > 0
})

/**
 * Determine if the action should be shown.
 *
 * @return {Boolean}
 */
const show = computed(() => {
    return uiStore.isActionActive(EMAIL_DRIVER)
})

/**
 * The on accept handler.
 *
 * @return {Promise}
 */
const onAccept = async () => {
    try {
        isLoading.value = true

        await driverStore.sendEmail(
            data.value.segment_id,
            selectedDrivers.value
        )

        toastStore.setNotification({
            message: 'Email(s) sent successfully',
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        isLoading.value = false

        onCancel()
    }
}

/**
 * The on cancel handler.
 *
 * @return {void}
 */
const onCancel = () => {
    uiStore.closeAction(EMAIL_DRIVER)
}
</script>

<template>
    <v-dialog
        data-test="confirmation-dialog"
        v-model="show"
        persistent
        width="480"
    >
        <v-card class="!rounded-lg">
            <v-card-item
                title="Email Trip Sheet to Driver(s)"
                density="compact"
                class="!py-2 !px-4 bg-gray-100 border-b"
            >
                <template v-slot:append>
                    <v-btn
                        size="x-small"
                        variant="text"
                        icon="fal fa-close"
                        color="primary"
                        class="rounded-md"
                        @click="onCancel"
                    >
                    </v-btn>
                </template>
            </v-card-item>
            <v-card-text class="!px-4" data-test="confirmation-dialog-body">
                <v-form v-if="hasDrivers">
                    <span class="text-base mb-5">
                        Select which drivers to email:
                    </span>
                    <v-checkbox
                        v-for="driver in drivers"
                        :key="driver.id"
                        v-model="selectedDrivers"
                        :label="driver.name"
                        :value="driver.id"
                        variant="outlined"
                        density="comfortable"
                    ></v-checkbox>
                </v-form>
                <div v-else>
                    There are no drivers assigned to the current load segment.
                </div>
            </v-card-text>
            <div v-if="hasDrivers" class="bg-gray-50 border-t p-2">
                <span class="text-xs">
                    Trip Sheets will be sent as PDF attachments to an email. The
                    Sheet will only include Stops that have been assigned to the
                    Driver receiving the email.
                </span>
            </div>
            <v-spacer class="border-b"></v-spacer>
            <v-card-actions class="px-4 justify-end">
                <v-btn
                    data-test="confirmation-dialog-btn-close"
                    variant="outlined"
                    @click="onCancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    v-if="hasDrivers"
                    :loading="isLoading"
                    :disabled="!selectedDrivers?.length"
                    color="primary"
                    variant="flat"
                    data-test="confirmation-dialog-btn-accept"
                    @click="onAccept"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
